<script setup lang="ts">
import type { EventSummaryResponse } from '@@/types/api';
import { formatCurrency } from '~/composables/format-currency';
import { formatDeadlineDate } from '~/composables/format-datetime';
import { formatDateRange } from '~/composables/format-date-range';
import { formatTimeRemaining } from '~/composables/format-duration';
import { useCardInterest } from '~/composables/use-card-interest';

const props = defineProps<{
  event: EventSummaryResponse;
}>();

// Per-card interest wiring (native). Seeds button state from the shared interest
// store; counts stay optimistic. Interested-only — cards never show "Going".
const {
  descriptor: interestDescriptor,
  interestedCount,
  goingCount,
  isPending: isInterestPending,
  snackbar: interestSnackbar,
  snackbarText: interestSnackbarText,
  snackbarColor: interestSnackbarColor,
  setInterest,
} = useCardInterest({
  source: 'native',
  eventId: computed(() => props.event.id),
  initialInterested: props.event.interested_count,
  initialGoing: props.event.going_count,
  title: props.event.title,
});

// Statuses during which a new attendee can still obtain the commit (discounted)
// price. Once the event moves past fundraising, only the purchase price is
// available, so showing the commit price + "Save" chip would be misleading.
const COMMIT_PHASE_STATUSES = new Set<string>([
  'Draft',
  'Pending Review',
  'Approved',
  'Fundraising',
]);

const dateRange = computed(() =>
  formatDateRange(
    props.event.start_datetime ?? '',
    props.event.end_datetime ?? '',
    props.event.timezone ?? '',
  ),
);

const isCommitPhase = computed(() =>
  COMMIT_PHASE_STATUSES.has(props.event.user_facing_status),
);

const displayPrice = computed(() =>
  formatCurrency(
    isCommitPhase.value
      ? getLowestCommitPrice(props.event)
      : getLowestPurchasePrice(props.event),
  ),
);

const timeRemaining = computed(() =>
  formatTimeRemaining(props.event.time_remaining ?? null),
);

// Guarded: the summary API does not send fundraising_deadline yet.
const deadlineText = computed(() =>
  props.event.fundraising_deadline && props.event.timezone
    ? `Ends ${formatDeadlineDate(props.event.fundraising_deadline, props.event.timezone)}`
    : null,
);

const placeholder = useEventPlaceholderImage();

const isFundraising = computed(
  () => props.event.user_facing_status === 'Fundraising',
);

const savingsText = computed(() => {
  // Savings are only meaningful while the commit price is still obtainable.
  if (!isCommitPhase.value) {
    return null;
  }
  // For multi-type events, use the lowest-priced type's savings
  const types = props.event.registration_types;
  if (types?.length) {
    // Find the type with the lowest commit total
    const lowestType = types.reduce((min, rt) =>
      rt.pricing.commit.total < min.pricing.commit.total ? rt : min,
    );
    if (!lowestType.pricing.has_dual_pricing || !lowestType.pricing.savings) {
      return null;
    }
    return `Save ${formatCurrency(lowestType.pricing.savings)}`;
  }
  // Fallback: event-level pricing
  if (!props.event.pricing.has_dual_pricing || !props.event.pricing.savings) {
    return null;
  }
  return `Save ${formatCurrency(props.event.pricing.savings)}`;
});
</script>

<template>
  <v-card
    :to="`/events/${event.id}`"
    variant="outlined"
    rounded="lg"
    class="event-card"
  >
    <v-img
      :src="event.cover_image?.card_url ?? placeholder"
      :alt="event.title"
      :aspect-ratio="16 / 9"
      cover
      class="bg-surface-variant"
    >
      <div class="d-flex justify-end pa-2">
        <EventsEventStatusBadge
          :status="event.user_facing_status"
          variant="elevated"
        />
      </div>
    </v-img>

    <v-card-title class="text-subtitle-1 font-weight-bold pb-1">
      {{ event.title }}
    </v-card-title>

    <v-card-subtitle class="pb-2">
      <v-icon size="small" class="mr-1">mdi-calendar</v-icon>
      {{ dateRange }}
      <br />
      <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
      {{ event.location_name ?? event.location_address ?? '' }}
    </v-card-subtitle>

    <v-card-text class="pt-0">
      <div class="text-body-2 text-medium-emphasis mb-2">
        {{ event.organizer?.name ?? 'Event Organizer' }}
      </div>

      <template v-if="isFundraising">
        <!--
          Wraps only between whole pieces: when the deadline/countdown block
          doesn't fit beside the percent it drops to its own (left-aligned)
          line, and within it the countdown may drop further. The separator
          rides on the deadline piece so it never starts or ends a line alone.
        -->
        <div
          class="d-flex flex-wrap align-center justify-space-between gc-2 mb-1"
        >
          <span class="text-body-2 text-medium-emphasis text-no-wrap">
            {{ event.progress_percentage ?? 0 }}% funded
          </span>
          <span
            v-if="deadlineText || timeRemaining"
            class="d-flex flex-wrap gc-1 text-body-2 text-medium-emphasis"
          >
            <span
              v-if="deadlineText"
              class="text-no-wrap"
              data-testid="event-card-deadline"
            >
              {{ deadlineText
              }}<template v-if="timeRemaining"> &middot;</template>
            </span>
            <span
              v-if="timeRemaining"
              class="text-no-wrap"
              data-testid="event-card-countdown"
            >
              {{ timeRemaining }}
            </span>
          </span>
        </div>
        <v-progress-linear
          :model-value="event.progress_percentage ?? 0"
          color="primary"
          bg-color="primary-container"
          rounded
          height="8"
          class="mb-3"
        />
      </template>

      <div class="d-flex align-center ga-2">
        <span class="text-body-1 font-weight-bold">
          From {{ displayPrice }}
        </span>
        <v-chip v-if="savingsText" color="tertiary" size="x-small" label>
          {{ savingsText }}
        </v-chip>
      </div>

      <!-- Social proof: always shown, reflects optimistic interest toggles. -->
      <EventsSocialProofCounts
        :interested-count="interestedCount"
        :going-count="goingCount"
        class="mt-3"
      />

      <!-- Interest toggle. Wrapped in a click guard so tapping the control never
           triggers the card's :to navigation to the detail page. -->
      <div
        v-if="interestDescriptor.visible"
        class="event-card__interest mt-2"
        @click.stop.prevent
      >
        <EventsEventInterestControl
          :descriptor="interestDescriptor"
          :event-id="event.id"
          :pending="isInterestPending"
          @select="setInterest"
        />
      </div>
    </v-card-text>
  </v-card>

  <v-snackbar
    v-model="interestSnackbar"
    :color="interestSnackbarColor"
    :timeout="4000"
    location="bottom"
  >
    {{ interestSnackbarText }}
  </v-snackbar>
</template>

<style scoped>
.event-card {
  transition: box-shadow 0.2s ease;
  text-decoration: none !important;
}
.event-card:hover {
  box-shadow: 0 4px 12px rgb(0 0 0 / 12%);
}
</style>
